import {skipToken} from '@reduxjs/toolkit/query';
import {DataStatus, Separator, Tooltip} from 'platform/components';
import {Box, getStepsFromPixels, Heading, HStack, Icon, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {isNotNil, without} from 'ramda';

import {
  useGetPersonnelOverviewBusinessCaseQuery,
  useGetPersonnelOverviewInterestQuery,
  useGetPersonnelOverviewWorkshopInterestQuery,
} from '@dms/api/core';
import {InterestStateSchema} from '@dms/api/finance';
import {BusinessCaseStateSchema} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps, useOnMount} from 'shared/web';

import {FormToDataGridConnectorProps, useAvailableHeightPx} from 'features/eag-grid';

import {ALL_BRANCHES_VALUE} from '../../constants/allBranchesValue';
import {useBranches} from '../../hooks/useBranches';
import {useGetCurrentBranch} from '../../hooks/useGetCurrentBranch';
import {PersonnelOverviewAllRow} from './components/PersonnelOverviewAllRow';
import {PersonnelOverviewBranchSelect} from './components/PersonnelOverviewBranchSelect';
import {PersonnelOverviewList} from './components/PersonnelOverviewList';
import {type PersonnelOverviewEntity} from './types/PersonnelOverviewEntity';
import {useHasDefaultBranchBeenSeeded} from './useHasDefaultBranchBeenSeeded';
import {useHasUserSelectedAllBranches} from './useHasUserSelectedAllBranches';
import {resolveBranchSelection} from './utils/resolveBranchSelection';
import {togglePersonnelOverviewUserId} from './utils/togglePersonnelOverviewUserId';

type PersonnelOverviewFilters = Partial<{
  owner: string[];
  branch: string | string[];
}>;

export type PersonnelOverviewProps = {
  entity: PersonnelOverviewEntity;
  isOpen: boolean;
} & FormToDataGridConnectorProps &
  TestIdProps;

const PANEL_WIDTH = 63;

// DataGrid hydrates its form values asynchronously; seeding the branch synchronously on mount
// races with that hydration and gets clobbered. Tracked as a follow-up to remove this delay.
const INITIAL_BRANCH_SEED_DELAY_MS = 1000;

export function PersonnelOverview(props: PersonnelOverviewProps) {
  const {availableHeightPx, callbackRef} = useAvailableHeightPx();
  const {branchOptions} = useBranches();
  const {activeBranchId} = useGetCurrentBranch();
  const [hasUserSelectedAll, setHasUserSelectedAll] = useHasUserSelectedAllBranches(props.entity);
  const [hasInitialSeedFired, setHasInitialSeedFired] = useHasDefaultBranchBeenSeeded(props.entity);

  const filterValues: PersonnelOverviewFilters = {...props.values};
  const selectedUserIds = filterValues.owner ?? [];

  const allBranchesOption = {
    value: ALL_BRANCHES_VALUE,
    label: i18n.t('entity.branch.labels.allBranches'),
  };
  const dropdownOptions = [allBranchesOption, ...branchOptions];

  const branchSelection = resolveBranchSelection({
    formBranch: filterValues.branch,
    hasUserSelectedAll,
    hasInitialSeedFired,
  });

  // While branchSelection is 'none' the form has nothing committed yet, so we
  // display activeBranchId as a default;

  const selectedBranchId = match(branchSelection)
    .with({kind: 'all'}, () => ALL_BRANCHES_VALUE)
    .with({kind: 'specific'}, ({branchId}) => branchId)
    .with({kind: 'none'}, () => activeBranchId)
    .exhaustive();

  const queryBranchId = match(branchSelection)
    .with({kind: 'all'}, () => undefined)
    .with({kind: 'specific'}, ({branchId}) => branchId)
    .with({kind: 'none'}, () => activeBranchId)
    .exhaustive();

  const interestQuery = useGetPersonnelOverviewInterestQuery(
    props.isOpen && props.entity === 'interest' ? {branchId: queryBranchId} : skipToken
  );
  const businessCaseQuery = useGetPersonnelOverviewBusinessCaseQuery(
    props.isOpen && props.entity === 'business-case' ? {branchId: queryBranchId} : skipToken
  );
  const workshopInterestQuery = useGetPersonnelOverviewWorkshopInterestQuery(
    props.isOpen && props.entity === 'workshop-interest' ? {branchId: queryBranchId} : skipToken
  );

  // Workaround: the DataGrid form is not ready synchronously at mount, so we add timeout to wait for the form to be ready.
  // Removing this requires the grid to expose a ready/onMount callback. Will be fixed in T20-124523.

  useOnMount(() => {
    if (branchSelection.kind === 'none' && isNotNil(activeBranchId)) {
      setTimeout(() => {
        props.onChange({
          ...props.values,
          branch: activeBranchId,
        });
        setHasInitialSeedFired(true);
      }, INITIAL_BRANCH_SEED_DELAY_MS);
    }
  });

  const activeBusinessCaseStates = without(
    [BusinessCaseStateSchema.enum.CLOSED, BusinessCaseStateSchema.enum.UNSUCCESSFUL],
    BusinessCaseStateSchema.options
  );
  const activeInterestStates = [InterestStateSchema.enum.NEW, InterestStateSchema.enum.OPEN];

  const handleSelectedUserIdsChange = (nextSelectedUserIds: string[]) => {
    const isEmpty = nextSelectedUserIds.length === 0;
    props.onChange({
      ...props.values,
      owner: nextSelectedUserIds,
      ...match(props.entity)
        .with('interest', 'workshop-interest', () => ({
          state: isEmpty ? undefined : activeInterestStates,
        }))
        .with('business-case', () => ({
          businessCaseState: isEmpty ? undefined : activeBusinessCaseStates,
        }))
        .exhaustive(),
    });
  };

  const handleBranchChange = (nextValue: string) => {
    const nextIsAllBranches = nextValue === ALL_BRANCHES_VALUE;
    setHasUserSelectedAll(nextIsAllBranches);
    props.onChange({
      ...props.values,
      branch: nextIsAllBranches ? undefined : nextValue,
      owner: [],
      ...match(props.entity)
        .with('interest', 'workshop-interest', () => ({state: undefined}))
        .with('business-case', () => ({businessCaseState: undefined}))
        .exhaustive(),
    });
  };

  const query = match(props.entity)
    .with('interest', () => interestQuery)
    .with('business-case', () => businessCaseQuery)
    .with('workshop-interest', () => workshopInterestQuery)
    .exhaustive();

  const items = query.data ?? [];

  const handleUserSelect = (userId: string) =>
    handleSelectedUserIdsChange(togglePersonnelOverviewUserId(selectedUserIds, userId));

  const handleClearUserSelection = () => {
    handleSelectedUserIdsChange([]);
  };

  if (!props.isOpen) {
    return null;
  }

  return (
    <Box
      ref={callbackRef}
      width={PANEL_WIDTH}
      height={availableHeightPx > 0 ? getStepsFromPixels(availableHeightPx) : undefined}
    >
      <Box
        height="100%"
        border="1px solid"
        borderColor="palettes.neutral.40.100"
        boxShadow="elevation_1"
        backgroundColor="general.white"
        overflow="hidden"
        data-testid={suffixTestId('personnelOverview', props)}
      >
        <VStack height="100%">
          <Box padding={3} paddingBottom={2}>
            <VStack spacing={3}>
              <HStack spacing={1} align="center">
                <Heading size={4}>{i18n.t('page.personnelOverview.title')}</Heading>
                <Tooltip description={i18n.t('page.personnelOverview.description')}>
                  <Icon value="action/info_outline" size={4} color="text.secondary" />
                </Tooltip>
              </HStack>
              <PersonnelOverviewBranchSelect
                value={selectedBranchId}
                options={dropdownOptions}
                onChange={handleBranchChange}
                data-testid={suffixTestId('personnelOverview', props)}
              />
            </VStack>
          </Box>
          <Box flex={1} overflowY="auto" paddingHorizontal={3} paddingBottom={3}>
            <DataStatus isLoading={query.isFetching} isError={isNotNil(query.error)}>
              <VStack spacing={1}>
                <PersonnelOverviewAllRow
                  items={items}
                  isSelected={selectedUserIds.length === 0}
                  onClick={handleClearUserSelection}
                  data-testid={suffixTestId('personnelOverview', props)}
                />
                <Separator orientation="horizontal" spacing={1} />
                <PersonnelOverviewList
                  items={items}
                  selectedUserIds={selectedUserIds}
                  onUserSelect={handleUserSelect}
                  data-testid={suffixTestId('personnelOverview', props)}
                />
              </VStack>
            </DataStatus>
          </Box>
        </VStack>
      </Box>
    </Box>
  );
}
